import React, { useRef, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import CircularProgress from '@mui/material/CircularProgress';
import { useGlobalState, useToasts } from 'Context';
import { v4 as uuidv4 } from 'uuid';
import './FileUploadBox.scss';

const DEFAULT_EXTENSIONS = ['.doc', '.docx', '.pdf', '.xls', '.xlsx'];
const DEFAULT_MAX_FILE_SIZE = 100 * 1024 * 1024; // 100 MB

// Magic-byte signatures for each allowed extension (first 8 bytes sufficient)
const MAGIC_BYTES: Record<string, number[][]> = {
  '.pdf':  [[0x25, 0x50, 0x44, 0x46]],             // %PDF
  '.doc':  [[0xD0, 0xCF, 0x11, 0xE0]],             // OLE2 compound document
  '.xls':  [[0xD0, 0xCF, 0x11, 0xE0]],             // OLE2 compound document
  '.docx': [[0x50, 0x4B, 0x03, 0x04]],             // ZIP (PK header)
  '.xlsx': [[0x50, 0x4B, 0x03, 0x04]],             // ZIP (PK header)
};

const readFileHeader = (file: File, length: number): Promise<Uint8Array> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = e => resolve(new Uint8Array(e.target!.result as ArrayBuffer));
    reader.onerror = reject;
    reader.readAsArrayBuffer(file.slice(0, length));
  });

const matchesMagicBytes = (header: Uint8Array, patterns: number[][]): boolean =>
  patterns.some(pattern => pattern.every((b, i) => header[i] === b));

export type UploadedFile = {
  serverId: string;
  filename: string;
  fileSize: number;
};

type UploadState = 'idle' | 'uploading' | 'uploaded';

type Props = {
  uploadEndpoint: string;
  allowedExtensions?: string[];
  maxFileSizeBytes?: number;
  initialFile?: UploadedFile;
  label?: string;
  note?: string;
  onChange: (file: UploadedFile | null) => void;
  onUploadStateChange?: (state: UploadState) => void;
};

const FileUploadBox: React.FC<Props> = ({
  uploadEndpoint,
  allowedExtensions = DEFAULT_EXTENSIONS,
  maxFileSizeBytes = DEFAULT_MAX_FILE_SIZE,
  initialFile,
  label,
  note,
  onChange,
  onUploadStateChange,
}) => {
  const toast = useToasts();
  const { token } = useGlobalState();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [uploadState, setUploadState] = useState<UploadState>(
    initialFile ? 'uploaded' : 'idle'
  );
  const [uploadedFile, setUploadedFile] = useState<UploadedFile | null>(
    initialFile ?? null
  );
  const [isDragging, setIsDragging] = useState(false);

  const updateState = (state: UploadState) => {
    setUploadState(state);
    onUploadStateChange?.(state);
  };

  const handleFile = async (file: File) => {
    const ext = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!allowedExtensions.includes(ext)) {
      toast.error(
        `Invalid file type. Only ${allowedExtensions.join(', ')} files are accepted.`
      );
      return;
    }

    if (file.size > maxFileSizeBytes) {
      const limitMB = Math.round(maxFileSizeBytes / (1024 * 1024));
      toast.error(`File size exceeds the ${limitMB} MB limit.`);
      return;
    }

    const magicPatterns = MAGIC_BYTES[ext];
    if (magicPatterns) {
      const header = await readFileHeader(file, 8);
      if (!matchesMagicBytes(header, magicPatterns)) {
        toast.error(
          `File content does not match its extension. Only ${allowedExtensions.join(', ')} files are accepted.`
        );
        return;
      }
    }

    updateState('uploading');

    const CHUNK_SIZE = 1024 * 1024 * 10;
    const fileId = uuidv4();
    const numChunks = Math.ceil(file.size / CHUNK_SIZE);

    // Some browsers (esp. on macOS) return empty file.type for .doc/.xls etc.
    // Fall back to extension-based MIME type to pass server validation.
    const MIME_BY_EXT: Record<string, string> = {
      '.pdf':  'application/pdf',
      '.doc':  'application/msword',
      '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      '.xls':  'application/vnd.ms-excel',
      '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    };
    const resolvedContentType = file.type || MIME_BY_EXT[ext] || 'application/octet-stream';

    const authHeaders: HeadersInit = { Authorization: `Bearer ${token}` };
    const sessionId = localStorage.getItem('sessionId');
    if (sessionId) (authHeaders as Record<string, string>)['X-Session-Id'] = sessionId;

    try {
      for (let chunk = 0; chunk * CHUNK_SIZE < file.size; chunk++) {
        const blob = file.slice(CHUNK_SIZE * chunk, CHUNK_SIZE * (chunk + 1));
        const formData = new FormData();
        formData.append('contentType', resolvedContentType);
        formData.append('fileName', file.name);
        formData.append('chunk', `${chunk}`);
        formData.append('chunks', `${numChunks}`);
        formData.append('id', fileId);
        formData.append('file', blob, file.name);

        const resp = await fetch(uploadEndpoint, {
          method: 'POST',
          headers: authHeaders,
          body: formData,
        });

        if (!resp.ok) throw new Error(`${resp.status}`);
      }

      const uploaded: UploadedFile = { serverId: fileId, filename: file.name, fileSize: file.size };
      setUploadedFile(uploaded);
      updateState('uploaded');
      onChange(uploaded);
    } catch {
      updateState('idle');
      toast.error('File upload failed. Please try again.');
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await handleFile(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    await handleFile(file);
  };

  const handleRemove = () => {
    setUploadedFile(null);
    updateState('idle');
    onChange(null);
  };

  return (
    <div className="file-upload-box">
      {label && <p className="file-upload-box__label">{label}</p>}

      <input
        ref={fileInputRef}
        type="file"
        accept={allowedExtensions.join(',')}
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />

      {uploadState === 'idle' && (
        <div
          className={`file-upload-box__idle${isDragging ? ' file-upload-box__idle--dragging' : ''}`}
          onClick={() => fileInputRef.current?.click()}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && fileInputRef.current?.click()}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <div className="file-upload-box__idle-left">
            <FileUploadOutlinedIcon className="file-upload-box__idle-icon" />
            <strong className="file-upload-box__idle-select">Select file</strong>
          </div>
          <div className="file-upload-box__idle-right">
            <span className="file-upload-box__idle-drop">Drop file here to upload</span>
            <span className="file-upload-box__idle-formats">
              {allowedExtensions.join(', ')}
            </span>
          </div>
        </div>
      )}

      {uploadState === 'uploading' && (
        <div className="file-upload-box__row">
          <div className="file-upload-box__row-content">
            <DescriptionOutlinedIcon className="file-upload-box__row-icon" />
            <span className="file-upload-box__row-name">Uploading...</span>
          </div>
          <CircularProgress size={24} thickness={4} className="file-upload-box__row-spinner" />
        </div>
      )}

      {uploadState === 'uploaded' && uploadedFile && (
        <div className="file-upload-box__row">
          <div className="file-upload-box__row-content">
            <DescriptionOutlinedIcon className="file-upload-box__row-icon" />
            <span className="file-upload-box__row-name">{uploadedFile.filename}</span>
          </div>
          <button
            className="file-upload-box__remove"
            onClick={handleRemove}
            aria-label="Remove file"
          >
            <CloseIcon className="file-upload-box__remove-icon" />
          </button>
        </div>
      )}

      {note && <p className="file-upload-box__note">{note}</p>}
    </div>
  );
};

export default FileUploadBox;
